import { useCallback, useMemo, useState } from 'react';
import type { FC, MouseEventHandler } from 'react';
import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import { isServer } from '@tanstack/react-query';
import parse from 'html-react-parser';
import { v4 as uuidv4 } from 'uuid';
import { RecommendationLocation } from '../../components/recommendations/RecommendationLocation';
import { ResponsiveImage } from '../../components/responsive-image';
import { SoftLink } from '../../components/soft-link/SoftLink';
import { usePDP } from '../../contexts/pdp/usePDP';
import { useProductsAddedToBagDrawer } from '../../contexts/products-added-to-bag-drawer';
import { EventAction, EventLocation, useBloomreach } from '../../hooks/useBloomreach';
import { checkBadgeVisibility } from '../../utils/badge-visibility';
import { buildPDPUrl } from '../../utils/product-utils';
import { Badge } from '../badge';
import { Card } from '../card';
import { Rating } from '../rating';
import { Text } from '../typography';
import { ProductPrice } from './ProductPrice';
import type { Product } from '../../bff-api/products/model';

export interface ProductCardProps {
    widgetId: string;
    widgetType: string;
    metadataType: string;
    product: Product;
    location: RecommendationLocation;
    onAddProductToBag: (product: Product) => void;
}

export const ProductCard: FC<ProductCardProps> = ({
    widgetId,
    widgetType,
    metadataType,
    product,
    location,
    onAddProductToBag,
}) => {
    const [clicked, setClicked] = useState(false);
    const { onCloseDrawer } = useProductsAddedToBagDrawer();
    const { setSelectedQuantity } = usePDP();
    const { widgetClicked } = useBloomreach();
    const { c_imageUrl, name = '', c_turntoTotalReviewCount = 0, c_turntoAverageRating } = product;
    const url = buildPDPUrl(product);
    const widthHeight = useBreakpointValue({ xs: '164px', sm: '200px' });

    const isBadgeVisible: boolean = useMemo(
        () =>
            checkBadgeVisibility({
                badgeEndDate: product.c_badge?.badgeEndDate,
                badgeStartDate: product.c_badge?.badgeStartDate,
                badgeLabel: product.c_badge?.badgeLabel,
                badgeShowOnPDP: product.c_badge?.badgeShowOnPDP,
                badgeShowOnPLP: product.c_badge?.badgeShowOnPLP,
                pageType: 'PDP',
            }),
        [product.c_badge],
    );

    const onClick: MouseEventHandler<HTMLDivElement> = useCallback(
        (e) => {
            setTimeout(() => {
                setClicked(true);
                if (e?.ctrlKey) {
                    // When user Ctrl + Click in a recommendation, it will open a new tab.
                    return;
                }

                setSelectedQuantity(1);
                onCloseDrawer();
                widgetClicked({
                    location: EventLocation.widget,
                    action: EventAction['widget-click'],
                    data: {
                        widgetResponseId: uuidv4(),
                        widgetId,
                        widgetType,
                        metadataType,
                        itemId: product.id,
                    },
                });
                if (!isServer) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            });
        },
        [
            product.id,
            onCloseDrawer,
            setSelectedQuantity,
            widgetClicked,
            widgetId,
            widgetType,
            metadataType,
        ],
    );

    const clickedIndicator = useMemo(
        () =>
            clicked ? (
                <Flex
                    data-testid={`product-clicked-indicator-${product.id}`}
                    position="absolute"
                    top={0}
                    right={0}
                    left={0}
                    bottom={0}
                    margin="0 !important"
                    backgroundColor="white"
                    opacity="0.7"
                    zIndex="2"
                />
            ) : null,
        [product.id, clicked],
    );

    if (location === RecommendationLocation.DRAWER) {
        return (
            <SoftLink to={url}>
                <Card
                    id={product.id}
                    padding="16px"
                    maxW="364px"
                    borderRadius="1rem"
                    onClick={onClick}
                    h="100%"
                >
                    <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        p="0"
                        h="100%"
                        position="relative"
                    >
                        {c_imageUrl ? (
                            <ResponsiveImage
                                id={`product-card-${product.id}-img`}
                                src={c_imageUrl}
                                alt={product.name}
                                width="100px"
                                height="100px"
                                className="mt-[1px] aspect-square"
                            />
                        ) : null}
                        <Box
                            ml="4"
                            alignItems="flex-end"
                            w="100%"
                            h="100%"
                            display="flex"
                            flexWrap="wrap"
                        >
                            <Box display="flex" flexGrow="1" h="48px" mb={4}>
                                <Text noOfLines={2}>{parse(name)}</Text>
                            </Box>
                            <ProductPrice
                                product={product}
                                location={location}
                                widgetId={widgetId}
                                widgetType={widgetType}
                                metadataType={metadataType}
                                onAddProductToBag={onAddProductToBag}
                                hideSuggLabel
                            />
                        </Box>
                        {clickedIndicator}
                    </Box>
                </Card>
            </SoftLink>
        );
    }

    return (
        <SoftLink to={url}>
            <Card
                id={product.id}
                maxW={{ base: '164px', sm: '200px' }}
                padding="0px"
                onClick={onClick}
                borderRadius="0.5rem"
                h="100%"
                display="flex"
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    p="0"
                    position="relative"
                    justifyItems="space-between"
                    h="100%"
                >
                    {isBadgeVisible && (
                        <Box position="absolute" top={4} left={{ base: 4, sm: 8, md: 4 }}>
                            <Badge text={product.c_badge?.badgeLabel || ''} />
                        </Box>
                    )}

                    {c_imageUrl && widthHeight ? (
                        <ResponsiveImage
                            id={`product-card-${product.id}-img`}
                            src={c_imageUrl}
                            alt={product.name}
                            width={widthHeight}
                            height={widthHeight}
                            className="mt-[1px] aspect-square rounded-t-[8px]"
                            loading="lazy"
                        />
                    ) : null}

                    <Box p="2" display="flex" flexDirection="column" flexGrow="1" width="100%">
                        <Flex className="space-x-2" alignItems="center">
                            <Rating
                                initialRating={
                                    c_turntoAverageRating ? parseFloat(c_turntoAverageRating) : 0
                                }
                                disabled
                                iconSize="sm"
                            />
                            <Text size="small">{`(${c_turntoTotalReviewCount})`}</Text>
                        </Flex>
                        <Box display="flex" h="48px" mt={1}>
                            <Text noOfLines={2}>{parse(name)}</Text>
                        </Box>
                        <Box mt={4} flexGrow="1">
                            <ProductPrice
                                product={product}
                                location={location}
                                widgetId={widgetId}
                                widgetType={widgetType}
                                metadataType={metadataType}
                                onAddProductToBag={onAddProductToBag}
                                hideSuggLabel
                            />
                        </Box>
                    </Box>
                    {clickedIndicator}
                </Box>
            </Card>
        </SoftLink>
    );
};
